import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useAccess } from '@hooks/access';
import { SchedulerSidebarEquipmentMember } from '@contexts/scheduler/sidebar/types';
import { PhoneSection } from '../section';
import { getConflicts } from '../multiDay/helpers';
import { ResourcePicker } from '../resourcePicker';
import { ResourceRow } from '../resourceRow';
import { PhoneResourcesProps } from '../crew/types';

const assetLabel = (name: string, internalId?: string) =>
  `${name}${internalId ? ` (${internalId})` : ''}`;

export const PhoneEquipment = ({
  open,
  onOpenChange,
  swapId,
  onSwap,
  multi,
  specialty,
}: PhoneResourcesProps) => {
  const { t } = useTranslation('app');
  const access = useAccess();
  const [collapsed, setCollapsed] = useState(false);
  const { equipmentMembers, disabled, setState, conflictsByAssetId } = useSchedulerSidebar();
  const {
    assetMembers: { options: assetOptions, disabled: assetsDisabled },
  } = useDropdownsContext();
  const readOnly = access.readOnly || disabled;
  const multiConflicts = (id: string, name: string) => {
    if (!multi) return {};
    const hits = getConflicts(multi.lookup, 'assets', id);
    return {
      conflictDays: new Set(hits.map((hit) => hit.date)).size,
      onShowConflicts: () => multi.onShowConflicts(name, hits),
    };
  };

  const assets = useMemo(() => assetOptions.filter((el) => el.id !== 'unassigned'), [assetOptions]);
  const options = assets.map((el) => ({
    value: el.id,
    label: assetLabel(el.name, el.internalId),
    conflicts: multi ? [] : conflictsByAssetId[el.id] || [],
    qualified: !specialty || Boolean(el.specialtyValues?.includes(specialty)),
    ...multiConflicts(el.id, assetLabel(el.name, el.internalId)),
  }));
  const selected = equipmentMembers
    .filter((el) => el.assetId && !el.discardedAt)
    .map((el) => el.assetId);

  const toMember = (assetId: string): SchedulerSidebarEquipmentMember => {
    const asset = assets.find((el) => el.id === assetId);
    return {
      id: undefined,
      assetId,
      name: asset?.name || '',
      internalId: asset?.internalId || '',
      conflicts: [],
      edit: false,
    };
  };

  const toggle = (assetId: string) => {
    if (swapId) {
      if (assetId !== swapId && !selected.includes(assetId))
        setState((prev) => ({
          ...prev,
          defaultEquipment: false,
          equipmentMembers: prev.equipmentMembers.map((el) =>
            el.assetId === swapId ? toMember(assetId) : el,
          ),
        }));
      onSwap(undefined);
      onOpenChange(false);
      return;
    }
    setState((prev) => ({
      ...prev,
      defaultEquipment: false,
      equipmentMembers: selected.includes(assetId)
        ? prev.equipmentMembers.filter((el) => el.assetId !== assetId)
        : [...prev.equipmentMembers, toMember(assetId)],
    }));
  };

  const remove = (assetId: string) =>
    setState((prev) => ({
      ...prev,
      defaultEquipment: false,
      equipmentMembers: prev.equipmentMembers.filter((el) => el.assetId !== assetId),
    }));

  return (
    <PhoneSection
      title={t('schedule.equipment.title', { count: equipmentMembers.length })}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}>
      {!collapsed && (
        <div className="flex flex-col gap-1.5">
          {!access.readOnly && (
            <ResourcePicker
              open={open}
              onOpenChange={(value) => {
                if (!value) onSwap(undefined);
                onOpenChange(value);
              }}
              options={options}
              selected={swapId ? [swapId] : selected}
              swapping={Boolean(swapId)}
              onToggle={toggle}
              addLabel={t('schedule.equipment.add')}
              searchPlaceholder={t('schedule.equipment.searchPlaceholder')}
              hint={t('schedule.equipment.updateHint')}
              conflictTitle={t('schedule.equipment.tooltip.conflict.title')}
              disabled={readOnly || assetsDisabled}
            />
          )}
          {equipmentMembers.map((el) => (
            <ResourceRow
              key={el.assetId}
              name={el.name}
              role={el.internalId ? `#${el.internalId}` : ''}
              conflicts={
                el.equipmentDeactivated || multi ? [] : (conflictsByAssetId[el.assetId] ?? [])
              }
              {...multiConflicts(el.assetId, assetLabel(el.name, el.internalId))}
              deactivated={el.equipmentDeactivated}
              disabled={readOnly}
              swapLabel={t('schedule.equipment.tooltip.replace')}
              removeLabel={t('schedule.equipment.tooltip.remove')}
              deactivatedLabel={t('scheduling.phone.deactivated')}
              onSwap={() => {
                onSwap(el.assetId);
                onOpenChange(true);
              }}
              onRemove={() => remove(el.assetId)}
            />
          ))}
        </div>
      )}
    </PhoneSection>
  );
};
